/**
 * Policy Attributes Editor Component
 * Form-based editor for XACML policy target attributes
 */

import { useState } from 'react'
import { Plus, Trash2, User, FileText, Zap, Globe } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAttributesByCategory } from '@/hooks/use-attributes'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type {
  ParsedXacmlAttributes,
  ParsedXacmlAttribute,
  XacmlCategory,
} from '@/types/policy'
import { getCategoryDisplayName } from '@/lib/xacml-parser'
import { cn } from '@/lib/utils'

/**
 * Props for PolicyAttributesEditor
 */
interface PolicyAttributesEditorProps {
  attributes: ParsedXacmlAttributes
  onChange: (attributes: ParsedXacmlAttributes) => void
  className?: string
}

/**
 * Available data types for attributes
 */
const DATA_TYPES = [
  { value: 'http://www.w3.org/2001/XMLSchema#string', label: 'String' },
  { value: 'http://www.w3.org/2001/XMLSchema#boolean', label: 'Boolean' },
  { value: 'http://www.w3.org/2001/XMLSchema#integer', label: 'Integer' },
  { value: 'http://www.w3.org/2001/XMLSchema#double', label: 'Double' },
  { value: 'http://www.w3.org/2001/XMLSchema#date', label: 'Date' },
  { value: 'http://www.w3.org/2001/XMLSchema#time', label: 'Time' },
  { value: 'http://www.w3.org/2001/XMLSchema#dateTime', label: 'DateTime' },
  { value: 'http://www.w3.org/2001/XMLSchema#anyURI', label: 'URI' },
]

/**
 * Available match functions
 */
const MATCH_FUNCTIONS = [
  { value: 'urn:oasis:names:tc:xacml:1.0:function:string-equal', label: 'String Equal' },
  { value: 'urn:oasis:names:tc:xacml:1.0:function:integer-equal', label: 'Integer Equal' },
  { value: 'urn:oasis:names:tc:xacml:1.0:function:boolean-equal', label: 'Boolean Equal' },
  { value: 'urn:oasis:names:tc:xacml:1.0:function:date-equal', label: 'Date Equal' },
  { value: 'urn:oasis:names:tc:xacml:1.0:function:string-regexp-match', label: 'Regex Match' },
]

import { CATEGORY_MAP, DATA_TYPE_TO_XACML } from './constants'

/**
 * Get icon component for category
 */
function CategoryIcon({
  category,
  className,
}: {
  category: XacmlCategory
  className?: string
}) {
  const iconClass = cn('h-4 w-4', className)

  switch (category) {
    case 'subject':
      return <User className={iconClass} />
    case 'resource':
      return <FileText className={iconClass} />
    case 'action':
      return <Zap className={iconClass} />
    case 'environment':
      return <Globe className={iconClass} />
    default:
      return null
  }
}

/**
 * Single value input with remove button
 */
function ValueInput({
  value,
  onChange,
  onRemove,
  dataType,
}: {
  value: string | number | boolean
  onChange: (value: string | number | boolean) => void
  onRemove: () => void
  dataType: string
}) {
  const isBoolean = dataType.includes('boolean')

  if (isBoolean) {
    return (
      <div className="flex items-center gap-2">
        <Select
          value={String(value)}
          onValueChange={(v) => onChange(v === 'true')}
        >
          <SelectTrigger className="w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">true</SelectItem>
            <SelectItem value="false">false</SelectItem>
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        value={String(value)}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 font-mono text-sm"
        placeholder="Value"
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  )
}

/**
 * Single attribute editor
 */
function AttributeEditor({
  attribute,
  onChange,
  onRemove,
  category,
}: {
  attribute: ParsedXacmlAttribute
  onChange: (attribute: ParsedXacmlAttribute) => void
  onRemove: () => void
  category: XacmlCategory
}) {
  // Fetch defined attributes for this category
  const { data: attributesData } = useAttributesByCategory(CATEGORY_MAP[category])
  const definedAttributes = attributesData?.data ?? []

  const handleAddValue = () => {
    const defaultValue = attribute.dataType.includes('boolean') ? true : ''
    onChange({
      ...attribute,
      values: [...attribute.values, defaultValue],
    })
  }

  const handleValueChange = (index: number, value: string | number | boolean) => {
    const newValues = [...attribute.values]
    newValues[index] = value
    onChange({ ...attribute, values: newValues })
  }

  const handleRemoveValue = (index: number) => {
    const newValues = attribute.values.filter((_, i) => i !== index)
    onChange({ ...attribute, values: newValues })
  }

  return (
    <div className="rounded-lg border bg-card p-4 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 grid gap-4 sm:grid-cols-2">
          {/* Attribute ID */}
          <div className="space-y-2">
            <Label className="text-xs">Attribute</Label>
            {definedAttributes.length > 0 ? (
              <Select
                value={attribute.attributeId}
                onValueChange={(value) => {
                  const selected = definedAttributes.find((a) => a.key === value)
                  onChange({
                    ...attribute,
                    attributeId: value,
                    // Auto-set data type if we found a matching attribute
                    dataType: selected
                      ? DATA_TYPE_TO_XACML[selected.dataType] || attribute.dataType
                      : attribute.dataType,
                  })
                }}
              >
                <SelectTrigger className="h-9 font-mono">
                  <SelectValue placeholder="Select attribute..." />
                </SelectTrigger>
                <SelectContent>
                  {definedAttributes.map((attr) => (
                    <SelectItem key={attr.id} value={attr.key}>
                      <span className="font-mono">{attr.key}</span>
                      <span className="ml-2 text-muted-foreground text-xs">
                        ({attr.displayName})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="space-y-2">
                <Input
                  value={attribute.attributeId}
                  onChange={(e) =>
                    onChange({ ...attribute, attributeId: e.target.value })
                  }
                  placeholder="e.g., role, department"
                  className="h-9 font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  No attributes defined.{' '}
                  <a href="/pac/attributes" className="text-primary underline">
                    Create one
                  </a>
                </p>
              </div>
            )}
          </div>

          {/* Data Type */}
          <div className="space-y-2">
            <Label className="text-xs">Data Type</Label>
            <Select
              value={attribute.dataType}
              onValueChange={(value) =>
                onChange({ ...attribute, dataType: value })
              }
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {DATA_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Match Function */}
          <div className="space-y-2 sm:col-span-2">
            <Label className="text-xs">Match Function</Label>
            <Select
              value={attribute.matchId ?? MATCH_FUNCTIONS[0].value}
              onValueChange={(value) =>
                onChange({ ...attribute, matchId: value })
              }
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select function" />
              </SelectTrigger>
              <SelectContent>
                {MATCH_FUNCTIONS.map((fn) => (
                  <SelectItem key={fn.value} value={fn.value}>
                    {fn.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-destructive"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Values */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Values</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={handleAddValue}
          >
            <Plus className="mr-1 h-3 w-3" />
            Add Value
          </Button>
        </div>
        <div className="space-y-2">
          {attribute.values.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              No values. Click &quot;Add Value&quot; to add one.
            </p>
          ) : (
            attribute.values.map((value, index) => (
              <ValueInput
                key={index}
                value={value}
                onChange={(v) => handleValueChange(index, v)}
                onRemove={() => handleRemoveValue(index)}
                dataType={attribute.dataType}
              />
            ))
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Category editor tab content
 */
function CategoryEditor({
  category,
  attributes,
  onChange,
}: {
  category: XacmlCategory
  attributes: ParsedXacmlAttribute[]
  onChange: (attributes: ParsedXacmlAttribute[]) => void
}) {
  const handleAddAttribute = () => {
    const newAttribute: ParsedXacmlAttribute = {
      attributeId: '',
      dataType: 'http://www.w3.org/2001/XMLSchema#string',
      values: [],
      matchId: 'urn:oasis:names:tc:xacml:1.0:function:string-equal',
    }
    onChange([...attributes, newAttribute])
  }

  const handleAttributeChange = (index: number, attribute: ParsedXacmlAttribute) => {
    const newAttributes = [...attributes]
    newAttributes[index] = attribute
    onChange(newAttributes)
  }

  const handleRemoveAttribute = (index: number) => {
    onChange(attributes.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CategoryIcon category={category} className="text-muted-foreground" />
          <span className="font-medium">
            {getCategoryDisplayName(category)} Attributes
          </span>
          {attributes.length > 0 && (
            <Badge variant="secondary">{attributes.length}</Badge>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddAttribute}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Attribute
        </Button>
      </div>

      {attributes.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="text-sm text-muted-foreground">
            No {category} attributes defined.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={handleAddAttribute}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add First Attribute
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {attributes.map((attr, index) => (
            <AttributeEditor
              key={index}
              attribute={attr}
              onChange={(a) => handleAttributeChange(index, a)}
              onRemove={() => handleRemoveAttribute(index)}
              category={category}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Main Policy Attributes Editor component
 */
export function PolicyAttributesEditor({
  attributes,
  onChange,
  className,
}: PolicyAttributesEditorProps) {
  const [activeTab, setActiveTab] = useState<XacmlCategory>('subject')

  const handleCategoryChange = (category: XacmlCategory, newAttributes: ParsedXacmlAttribute[]) => {
    onChange({
      ...attributes,
      [category]: newAttributes,
    })
  }

  const categories: XacmlCategory[] = ['subject', 'resource', 'action', 'environment']

  // Count total attributes
  const totalCount = categories.reduce(
    (sum, cat) => sum + attributes[cat].length,
    0
  )

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Policy Target</CardTitle>
          {totalCount > 0 && (
            <Badge variant="secondary">
              {totalCount} attribute{totalCount !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Define the conditions that determine when this policy applies
        </p>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as XacmlCategory)}>
          <TabsList className="grid w-full grid-cols-4">
            {categories.map((category) => (
              <TabsTrigger
                key={category}
                value={category}
                className="flex items-center gap-1"
              >
                <CategoryIcon category={category} className="h-3 w-3" />
                <span className="hidden sm:inline">{getCategoryDisplayName(category)}</span>
                {attributes[category].length > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 justify-center">
                    {attributes[category].length}
                  </Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
          {categories.map((category) => (
            <TabsContent key={category} value={category} className="mt-4">
              <CategoryEditor
                category={category}
                attributes={attributes[category]}
                onChange={(attrs) => handleCategoryChange(category, attrs)}
              />
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  )
}

/**
 * Create empty attributes structure
 */
export function createEmptyAttributes(): ParsedXacmlAttributes {
  return {
    subject: [],
    resource: [],
    action: [],
    environment: [],
  }
}

export default PolicyAttributesEditor
